import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { nestApi } from "@/lib/api";
import { isValidPhoneNumber } from "libphonenumber-js";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { DialCodePicker } from "@/components/ui/dial-code-picker";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Upload, X, Camera, FileText, Calendar, Check } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";


const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};
const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

export default function ProfilePage() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const certFileRef = useRef(null);
  const pictureUrlRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", 
    phone: { code: "+39", number: "" },
    whatsapp: { code: "+39", number: "" },
    dob: "",
    languages: [{ language: "", proficiency: "" }],
    cities: [{ city: "", years: "" }],
    tour_types: [],
    other_tour_type: "",
    is_certified: null,
    certify_info: false,
    has_picture: false,
    certification_file: null,
    certification_filename: null,
  });
  const [saving, setSaving] = useState(false);
  const [certUploading, setCertUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [languageOptions, setLanguageOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [tourTypeOptions, setTourTypeOptions] = useState([]);
  const [proficiencyOptions, setProficiencyOptions] = useState([]);
  const [pictureObjectUrl, setPictureObjectUrl] = useState(null);

  const fetchOptions = useCallback(async () => {
    try {
      const [langsRes, citiesRes, tourTypesRes, profRes] = await Promise.all([
        nestApi.get("/general/language"),
        nestApi.get("/general/city/cityWithCountryForRahePortal"),
        nestApi.get("/general/tour-type"),
        nestApi.get("/general/proficiency"),
      ]);
      setLanguageOptions(asArray(langsRes.data));      // [{ code, name }]
      setCityOptions(asArray(citiesRes.data));         // [{ id, name }]
      setTourTypeOptions(asArray(tourTypesRes.data));  // [{ id, code, name }]
      setProficiencyOptions(asArray(profRes.data));    // [{ id, level, code }]
    } catch {
      toast.error("Failed to load form options");
    }
  }, []);

  const loadPicture = useCallback(async () => {
    try {
      const res = await nestApi.get("/rahe-portal/profile/picture", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setPictureObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        pictureUrlRef.current = url;
        return url;
      });
    } catch {
      pictureUrlRef.current = null;
      setPictureObjectUrl(null);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await nestApi.get("/rahe-portal/profile");
      setProfile(data);
      setForm({
        full_name: data.full_name || "",
        email: data.email || "",
        phone: data.phone || { code: "+39", number: "" },
        whatsapp: data.whatsapp || { code: "+39", number: "" },
        dob: data.dob || "",
        languages: asArray(data.languages).length ? asArray(data.languages) : [{ language: "", proficiency: "" }],
        cities: asArray(data.cities).length ? asArray(data.cities) : [{ cityId: null, cityName: "", years: "" }],
        tour_types: asArray(data.tour_types),
        other_tour_type: data.other_tour_type || "",
        is_certified: data.is_certified,
        certify_info: false,
        has_picture: data.has_picture || false,
        certification_file: data.certification_file || null,
        certification_filename: data.certification_filename || null,
      });
      if (data.has_picture) loadPicture();
    } catch {
      toast.error("Failed to load profile");
    }
  }, [loadPicture]);

  useEffect(() => {
    fetchProfile();
    fetchOptions();
    return () => {
      if (pictureUrlRef.current) URL.revokeObjectURL(pictureUrlRef.current);
    };
  }, [fetchProfile, fetchOptions]);

  // Returns the errors object directly so callers don't rely on stale state
  const validateForm = () => {
    const newErrors = {};

    if (!form.full_name.trim()) newErrors.full_name = true;
    if (!form.email.trim()) newErrors.email = true;
    if (form.email && !isValidEmail(form.email)) {
      newErrors.email = true;
      newErrors.email_invalid = true;
    }
    if (!form.phone.number.trim()) {
      newErrors.phone = true;
    } else {
      const phoneCode = form.phone.code.startsWith("+") ? form.phone.code : `+${form.phone.code}`;
      const fullPhone = `${phoneCode}${form.phone.number.replace(/\D/g, "")}`;
      if (!isValidPhoneNumber(fullPhone)) {
        newErrors.phone = true;
        newErrors.phone_invalid = true;
      }
    }

    if (!form.whatsapp.number.trim()) {
      newErrors.whatsapp = true;
    } else {
      const waCode = form.whatsapp.code.startsWith("+") ? form.whatsapp.code : `+${form.whatsapp.code}`;
      const fullWhatsapp = `${waCode}${form.whatsapp.number.replace(/\D/g, "")}`;
      if (!isValidPhoneNumber(fullWhatsapp)) {
        newErrors.whatsapp = true;
        newErrors.whatsapp_invalid = true;
      }
    }
    if (!form.dob) newErrors.dob = true;
    
    const hasValidLanguage = form.languages.some(l => l.language && l.proficiency);
    if (!hasValidLanguage) newErrors.languages = true;
    
    if (!form.cities[0]?.cityId || !form.cities[0]?.years) newErrors.cities = true;
    
    if (form.tour_types.length === 0) newErrors.tour_types = true;
    if (form.tour_types.includes("OTHER") && !form.other_tour_type?.trim()) newErrors.other_tour_type = true;
    if (form.is_certified === null) newErrors.is_certified = true;
    if (!form.certify_info) newErrors.certify_info = true;
    
    setErrors(newErrors);
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      if (validationErrors.phone_invalid) {
        toast.error("Phone number is not a valid international number");
      } else if (validationErrors.whatsapp_invalid) {
        toast.error("WhatsApp number is not a valid international number");
      } else if (validationErrors.email_invalid) {
        toast.error("Please enter a valid email address");
      } else {
        toast.error(t("profile.allFieldsRequired"));
      }
      return;
    }
    
    setSaving(true);
    try {
      await nestApi.put("/rahe-portal/profile", form);
      toast.success("Profile updated successfully!");
      fetchProfile();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update profile. Please try again.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const { data } = await nestApi.post("/rahe-portal/profile/picture", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setForm(f => ({ ...f, has_picture: true }));
      loadPicture();
      toast.success("Profile picture uploaded!");
    } catch {
      toast.error("Failed to upload image");
    }
  };

  const toggleTourType = (type) => {
    setForm((f) => ({
      ...f,
      tour_types: f.tour_types.includes(type)
        ? f.tour_types.filter((t) => t !== type)
        : [...f.tour_types, type],
    }));
  };

  const updateLanguage = (index, field, value) => {
    const langs = [...form.languages];
    langs[index] = { ...langs[index], [field]: value };
    setForm((f) => ({ ...f, languages: langs }));
  };

  const updateCity = (index, field, value) => {
    const cities = [...form.cities];
    cities[index] = { ...cities[index], [field]: value };
    setForm((f) => ({ ...f, cities }));
  };

  const canAddLanguage = () => {
    const lastLang = form.languages[form.languages.length - 1];
    return lastLang?.language && lastLang?.proficiency;
  };

  const canAddCity = () => {
    const lastCity = form.cities[form.cities.length - 1];
    return lastCity?.cityId && lastCity?.years;
  };

  const formatJoiningDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd MMMM yyyy");
    } catch {
      return "N/A";
    }
  };

 const generateAssociationLetter = () => {
    if (!profile?.profile_complete) {
      toast.error("Please complete your profile first");
      return;
    }
 
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Elegant header with gradient effect simulation
    doc.setFillColor(26, 27, 46);
    doc.rect(0, 0, pageWidth, 50, 'F');
    
    // Add a subtle accent line
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 50, pageWidth, 3, 'F');
 
    const logoBase64 = 'iVBORw0KGgoAAAANSUhEUgAAA+gAAAEsCAYAAABQRZlvAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAEv2lUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSfvu78nIGlkPSdXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQnPz4KPHg6eG1wbWV0YSB4bWxuczp4PSdhZG9iZTpuczptZXRhLyc+CjxyZGY6UkRGIHhtbG5zOnJkZj0naHR0cDovL3d3dy53My5vcmcvMTk5OS8wMi8yMi1yZGYtc3ludGF4LW5zIyc+CgogPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9JycKICB4bWxuczpBdHRyaWI9J2h0dHA6Ly9ucy5hdHRyaWJ1dGlvbi5jb20vYWRzLzEuMC8nPgogIDxBdHRyaWI6QWRzPgogICA8cmRmOlNlcT4KICAgIDxyZGY6bGkgcmRmOnBhcnNlVHlwZT0nUmVzb3VyY2UnPgogICAgIDxBdHRyaWI6Q3JlYXRlZD4yMDI0LTEyLTEwPC9BdHRyaWI6Q3JlYXRlZD4KICAgICA8QXR0cmliOkV4dElkPjUxMTk1ZTRkLTQ2ZmMtNDdiNC04MDI5LTJhYjRkYzg2NTlkMTwvQXR0cmliOkV4dElkPgogICAgIDxBdHRyaWI6RmJJZD41MjUyNjU5MTQxNzk1ODA8L0F0dHJpYjpGYklkPgogICAgIDxBdHRyaWI6VG91Y2hUeXBlPjI8L0F0dHJpYjpUb3VjaFR5cGU+CiAgICA8L3JkZjpsaT4KICAgPC9yZGY6U2VxPgogIDwvQXR0cmliOkFkcz4KIDwvcmRmOkRlc2NyaXB0aW9uPgoKIDxyZGY6RGVzY3JpcHRpb24gcmRmOmFib3V0PScnCiAgeG1sbnM6ZGM9J2h0dHA6Ly9wdXJsLm9yZy9kYy9lbGVtZW50cy8xLjEvJz4KICA8ZGM6dGl0bGU+CiAgIDxyZGY6QWx0PgogICAgPHJkZjpsaSB4bWw6bGFuZz0neC1kZWZhdWx0Jz5IdW1yYWhlIExvZ28gLSBIb3Jpem9udGFsIC0gSG9yaXpvbnRhbCBXaGl0ZSBMb2dvPC9yZGY6bGk+CiAgIDwvcmRmOkFsdD4KICA8L2RjOnRpdGxlPgogPC9yZGY6RGVzY3JpcHRpb24+CgogPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9JycKICB4bWxuczpwZGY9J2h0dHA6Ly9ucy5hZG9iZS5jb20vcGRmLzEuMy8nPgogIDxwZGY6QXV0aG9yPkthemltIFIuIFJpenZpPC9wZGY6QXV0aG9yPgogPC9yZGY6RGVzY3JpcHRpb24+CgogPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9JycKICB4bWxuczp4bXA9J2h0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8nPgogIDx4bXA6Q3JlYXRvclRvb2w+Q2FudmEgKFJlbmRlcmVyKSBkb2M9REFHWXVsSU44NUkgdXNlcj1VQUV5dTFmQjlNTTwveG1wOkNyZWF0b3JUb29sPgogPC9yZGY6RGVzY3JpcHRpb24+CjwvcmRmOlJERj4KPC94OnhtcG1ldGE+Cjw/eHBhY2tldCBlbmQ9J3InPz5ZaGk4AAA3D0lEQVR4nOzdr48VVxjH4e/LNkHhsGtImtCAwEBSQSqoaFLbP6GupAkCKhqQpHWIlRgkEtcmIJqKJmAqIN2kCQa7bhVicyp2BGm4/Jw759zL8zi4d2ferOKTd+ZQAQAAALqr3gMAAAAAAh0AAACGINABAABgAAIdAAAABiDQAQAAYAACHQAAAAYg0AEAAGAAAh0AAGbQWrucZKf3HG9wVFV/9h4CWO2z3gMAAMCW+DbJT72HeINfkwh0GJgNOgAAzKC1dirJsyS7vWd5jRdJzlXVYe9BgNVO9B4AAAC2wRS/N3rPscINcQ7js0EHAIAZtdYeJrnSe45XPKqqr3sPAbydQAcAgBm11s4meZoxDow7SnK+qvZ7DwK8nUfcAQBgRlMM7/WeY7InzmFz2KADAMDMpgPjnic53XGMgyRnvHsOm8MGHQAAZjZF8fXOY1wX57BZbNABAGBNWmuPk1zscOsnVXWpw32Bj2CDDgAA6/NDjg9qW9LRdF9gwwh0AABYk6p6kuTuwre9O90X2DAecQcAgDVqrZ1O8k+WOTDuIMkXVXWwwL2AmdmgAwDAGk2xfHOh290U57C5bNABAGDNWms7Sf7Keg+Me5Lky6pa+p13YCYCHQAAFtBau5jk8Rpvccm757DZPOIOAAALmOL53pouf0+cw+azQQcAgIVMB8Y9T3JqxsseJjnj3XPYfDboAACwkCmib8182VviHLaDDToAACxoOjDuaZKzM1xuP8l5B8PBdrBBBwCABU0xfXWmy10V57A9BDoAACysqh4luf+Rl7k/XQfYEh5xBwCADlpru0me5cMOjDtMcq6qXsw7FdCTDToAAHQwxfXtD/zx2+Icto8NOgAAdNJaO5nk77zfgXH7SS5U1cv1TAX0YoMOAACdTJF97T1/7Jo4h+0k0AEAoKOq+i3Jg3f8+oPp+8AW8og7AAB0Nh0Y92+Sk2/42sskn3v3HLaXDToAAHQ2Rfedt3ztF3EO280GHQAABtBaO5Xj/3Zt9zUfv8jx9ty757DFbNABAGAAVXWY5MaKj38U57D9bNABAGAgrbWHSa688le/V9U3veYBliPQAQBgIK21s0meJtnJ8cFwF6pqv+9UwBI84g4AAAOZYnxv+uMdcQ6fDht0AAAYzHRg3B9JvpreTQc+AQIdAAAG1Frbqaqj3nMAyxHoAAAAMACBDgAAAAMQ6AAAADAAgQ4AAAADEOgAAAAwAIEOAAAAAxDoAAAAMACBDgAAAAMQ6AAAADAAgQ4AAAADEOgAAAAwAIEOAAAAAxDoAAAAMACBDgAAAAMQ6AAAADAAgQ4AAAADEOgAAAAwAIEOAAAAAxDoAAAAMACBDgAAAAMQ6AAAADAAgQ4AAAADEOgAAAAwAIEOAAAAAzix6oPW2uW2GX5e8hcGAAD/11r7vvc/it/Rd71/V8BqKwMdAAAAWI5ABwAAgAEIdAAAABiAQAcAAIABCHQAAAAYgEAHAACAAfwHAAD//+zXsQAAAADAIH/raewoiwQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgQdAAAABgIAAP//7N173GZzvf/x14cwYxjnUxjb+UxE5DTOZ6EiFVskh+zKobKTX7V/RbUVQkJkJ4kQheR8HKdsxswQMc4jtrMxQ1t57z++6+ae231f91rXvdb6ru+6Ps/H43rMI/e61nrfq/ta1/f89Qq6c84555xzzjnXAB/o8DPVlmJk3qnzYpI+CKwJrJW9lgHmBcb0e82XHf46MKPfazrwAvAI8DgwFZhqZo/V+Cs452ogaVlgfeDDwDrAQoRnxTz9/n2TWZ8RM4CXgYnAFGCSmU2qPXxEkhYAVgNWAf4FWBRYGFgw+3chYIlB3jqT99/Ll4B7gcnA/Wb2UMXxXYUkLUj4TH0IWBpYHFiE8PewGDAWmEb4nn2O8BmaCjxoZrfEyFwGSfMQPhOrZq/VCL/33ANe82dveYPBPwuPA49l/z5OKH+8Udsv4npW9n24FrAG4Ttxcd4rM8+d/Ts2O/xVwt9s3zN9OvA33is7P0r4232mxl/B9SNpYWD57LUcsAIwjlCuGZW9Rvf7d0z21jcI5Z6Z2b99ZaCXgKeBZ/r/a2aP1/MbNU+nCroDJK0ObA9sC3yE974A8xjLew+cTtcAuBG4Abgl5YKEc70qq1juAnwC2BRYIMfbRmevhQf89+36nRfgPuB64GIzu6uMvE0haQVgs+y1EbBil6fqq6QsMuC/79DvWgB3Arfz3vN2epfXcxWTtBSwN7AhsC6hUj6cJbMXhO/uvnO9DlwF/B641MzeKjdteSQtCexOyL82sFTBU8yTvfJc62+EBqyJwAPAZDO7r+D1nJuFpA0J5eatCZ/dMZ3fMYv5yVHWlvQGcAvhWX6zmd3TRVQ3DEnrABtkrzWBlQidDN3I/WzKrg3wIOH5dG/fv2b2SpfXT4YN9QNJmwC31pilW0eb2ffLPKGkXYGdCIXkcWWeu4BrgXOBS8zszUgZnHMdSBoLfBrYA9iqpstOA35LqKzfXtM1SyNpFKEhY2dgC/JVuqrU1/hxjpk9GDlLz8v+Pj4B7Ev4TFUxFe8l4OfAKWb2bAXnL0TSbITGqZ2y15pxE/EW8AfgV8CfzOwfkfMkQ9IBhL+tptvDzC4u62SS5iY0Ku1MKDvnaaAu2wzgGsKz/PII109eNkppc2BjYD1Cw3kTPQncDVwGXGtmL0TOU7pOFfRNCS1TTXeMmR070pNkQ8i+ABxGvEr5YF4HLiA8cO6MEUDSqoQWrKY7ycwOjx1iOJL2B86OnSOHPc3sorwHSxpHeGg23RlmdvBITpA9L47IXvMNc3iVpgInEJ4PjW7Ik7Qe8CVC5atIb0qdbgBOM7NLYgfpT9IUYPXYOYbxlJkt0+2bJc0PfBM4hPr+Pq4xs+2GP6wakpYGDgL2Z/BpG03wCnAecJ6Z3V3WSSV9m9BA13Q7mtnMvAf3WgVd0iKEcvPBhGlITfE8oZPrbDN7OHaYJstGCu9MaDj/KOmtTyZCz/rVwJ+ACW1oVOz5Ie6SFic8XA6i2PD1uowFDgQOlHQXcKSZTYicybnBpLJuRdeyXoIvA18nTg/BQMsDPwWOlXQacLKZPR850ywkfRL4KmF4XNNtCWwp6SngB2b2s9iBMkM2pjdIV4U6SaOBwwmfqbobu2oviEr6ALAboUNgG5r//+0ChIa1L0maBJxCqKyPdIrA6sD4kYarQc+XkweTTU06CtgHmCtynMEsBnwN+Jqky4GjzOwvkTM1hqQteG/Ew7KR44yUEdb5WQf4d+A1SecTGmf+O2qyEUitlaQ0ksZKOh54gvCQaWLlfKANgNskXZ71ajvnaiJpX0KP9fdpRuW8v/mBo4HnJJ0kKXoPtaQvSpoKXEQalfP+xgGnSZosqQlD/FJo/CqcMftMPQkcS5yRKLXdV0lzSzqaMEXlIsL83KZXzgdai9A7/JykH2UjALqVwt80pJOzFpKWlvQr4K/AATSzcj7QLsAUSb/IFnruSZIWlfRNSY8RRot9ifQr54OZjzAS6x5J90s6LFvULimdKuipPJQKr+Iu6euEivlXSePhMtDOwIOSTo0dxLl+UmnwK1QolrSKpDuB/yKsPNt0XwEekrRt3ReWZJL2lvQ0oWd/ubozlGwN4GZJv5EUcwhyChW53BklzSfpUsJnauCifnWq5b5KOpJQ5jiWsCtB6uYDjgSeyj4bPVvp6RX9OrWeIizcmMIzqb/ZgP2AaZK+ETtMnSTtJOkSwrD/79HOSvlQ1gJOBF6QdIGk1WIHyiuVAnUnRQoFH8sKjj+keT1g3ThU0iRJK8cO4hzpNOrlzinpR8BfSK8HeCngaknn1dVyLGlt4B7CwlJFV51uur2ARyR9PnaQ1GWrO08mDPNuLUlzSjpU0jTgR8RtiKjSXsDDkr6WDd/PK5UKXio5KyPpMMJIl6/GzlKS4yRdL2mx2EGqJOnLkp4ErgA+HjtPA3wKeEDSZdnK9I3WqYKeykNp2JySRkk6k7C9StsKjmsC90k6KHYQ1/NSqaAPS9I4SX8m9BKl7LOEwvP2wx45AtnQ3YmE7XTaagxwlqTfZqv31ymFz9awo9mygv4dxF+5v0/hEXh5ZIW/KcCpQC/0Ls8D/CdhGPGWscO4cmRDoq8j9ECmMA20iC0Jf6+1jzSrkqQ5JB2craPyE5q16HVT7ArcK+nKbPHaRmpDD3pHktYEJhEWZGmr0cDpks7OtmtxznVJ0g7A/YQtRtpgQeAqSUeVfWJJi0m6jTB0t1fsQZhitFGN10yhwXzI755s6sNphIJ+k5T+fSnpGMJ+vSuWfe4ErAxcL+msHMem0OgE6eQslaStCLv31LV9aAwLA3+q4ruxbpI+kO0g8AjwM5rTCNpkOwJ/lnRhtmB4o7RhDvqQObNe5Un0zhfl/sDvFfaRda5uKVQiOpJ0LPBH2tdbAPCDrPd3dBknk7Q+odd84zLOl5glgQmSto4dpOkkzQVcSli0p7UkLSfpbuC7sbM0wOcl3Sqp08J/qXxfpJKzNJJOBK4DFoqdpQZG+G78aewg3cpGATxMWMCx660ue9iehJGGh8UO0l9rh7hL+i5wes1ZmmBn4FZJTdqP0vWGVBr1BiXpZMJK6G22B3DHSBd1krQPcDdpLJpXpcuznqaqpfDZet9w8ayx+BrCkMImKmWIu6R/JQxpX7+M87XEJsBdklJfKLJnZL2wlxC2Hu41X8zmJiezcLSkBbIV9a8m/QVZYxsLnJit+r5W7DDQwiHu2VC6M4FjYmeJaD3gRjVgqyXXU1J5nszSqJc9M84mbDnSC9YmNOIV7h3J7tXxwLnlx0rSKOCKGubdptBgPsvnX9LshHVfmrBN3VBG9MySNLukU4BfEqaauVmtTNjqKLVFNntONrLqSnp7MbFdgYtTmCoqaW/CcPa9Y2dpmbWA+yV9L3aQxv8RFpGtIHox7Z5vntdawKUpPGhca1Sy4FIFBvZGnkuYHtJLlgOukTRvwfddRntW8i3LKOBKSR+NHaQpJBlhNf9WLcDUX9bAdT3wb7GzNNwCwJ2Sthnw31MYFQLp5OyapPmBm2jx57WAnQm7LjSSpCUlXUN4vvbCFIRYvpmt9B9t//S2Vd7Oobdb/wbaBvhx7BCuZ6TyPHm3N1LST+jdFuh1CQvkzDncgZJGZ6v5fqz6WEkaRVj/o6qFeVKoJPTPeBzw6VhBCujqvmZbCk4Expcbp9UuHbC1UQqjQiCdnF2RNAdhGspHYmdpkMMl7Rc7xECSNiUsYDuwsctVY0tgoqQon43WLBIn6Uh6t6DdyWG+d6+rSWrPjH2BL0fOEttGhFFHQ8rmEV9Lu1fzLcMihOHuVQx1TqGSYACSdgT+PXKWvArfV0k7EyrnbduytWpjCKN2Vsj+d1LfFy12Jr52wmB+IWnz2CH6SDocuAHvNa/bksBtkg6t+8Kp9Hh1JGk7wh6cbnCnSlo+dgjnmiJrEf157BwNsUu2qOb7ZNOGfk9vrtTejbWAC7Ih3mVKoZKgbKuaX8cOUhVJOwGXx86RsIUJ27AtRhqNTlA8ZwqfVQif18OAz8UO0mDnSxobM4CkuSRdAJwAfCBmlh42B6EedUqdF21DBX0l4CLa8btUZRRwXuwQrvVSmYO+BKHSOUfsIA1yzBBzqM/D5yUW9TF6Z8HB/gScT1pbFP4z74FZ5fyKCrP0inGELbzmiR0kp6IV7lQaHrYFTowdouGWAE6NdXFJSwJ3Ap+KlcHN4t8k1dax04Zt1vYFii501Is2lPT/YodwrTZ77AA5fQzfHmwwF/bf+UHSt/CCQbeOL3nUUgrfx+OALWKHKCjXMyurnF9WcZZesgawQ+wQOaXw2evGgbEDJGIfSbVvEylpGWAC8KG6r+06OkDSb7JdSirVhjnoLr//L2ml2CGcc420NFlvQTYF4Ntx4yRtTuA3votG+iTtRug59+GlzvWms+rctrhf5XyZuq7pCtkLuCibAliZNvSgu2IGnWvqXAm8US99n5O0D3AJPm1opNYHvhk7hOuepHUJw/adc71rYeCoOi6UjbyaQFiczDXX7oTp1ZXxAljv2TPbIsa5snmjXjuci69QXZZjKtx6zVUo68W6GqhiVX7nXFqOkFTpCuqSlgNuxivnqdhN0hlVndwr6L3p2NgBnHOuB8yJj1pKTrZy83WEnjPnnBsDHF3VybMF4W7FK+epOTDbDaF0Pge9N+0kab3YIZxzrgfsI2mV2CFcPpLmIGyltsJwxzrnesoRkhYt+6SSRgN/BD5Y9rldLU6QtHvZJ/U56L3ri7EDOOdcD5gNOC52CJfbD4DNYodwzjXSfhWc8wJgrQrO6+phwPnZ4rql8SHuveszda5K6XqCj7pxbnC7S/LtchpO0obA4bFzOOca65AyTybpx4StX13aRhFWdi+tXuUV9N41F/DZ2CFcq/ioG+eG5hW/BssKVr/Fn2POuaEtI2l8GSeS9AXgiDLO1WCvAk8BUwhz7P8MPAw8B8yMmKsK44CTyjqZ7+vZ2w4AzowdwjnnesCnJB1hZi/FDuIGdSrgK+4754ZzAGG19a5JWp12lb9fIdyT+4F7gSlm9thwb8oaRj+UvdYhbE+a8nD/AyRdZmZXjvREnSroPly1/daXtIqZPRQ7iHPOtdxcwIHA92MHcbOStAvwudg5nHNJ+KSkg81sxgjO8cvS0sRzL3AZcI2Z3dXNCbJ7OCF7ASBpKWBHYCfSHP5/jqRVR9oY74vEuY/HDuCccz3iYEk+taxBJM0NnB07h3MuGaOA7bt9s6SjgQ+XF6d2twLbmtmHzey73VbOh2Jmz5jZmWa2K7AScF6Z56/BIsDPRnoSLyi40rcGcD3rndgBnGu4ccB2sUO4WXyDUKByzrm8uio7S1oNOLbkLHWZCmxhZpuZ2bV1XNDMHjGzfQgV9VvruGZJ9pC0yUhO4BV0t56kJWKHcK3gzxPnhvfJ2AHcu+YCvhY7hHMuObtImr3IG7LRU+dXlKdK7xAWP1vDzG6KEcDMHgHGA18B3oyRoQs/HsmbvUDtwAuMzjlXl12LFuxcZVYkVNKdc66IscBWBd9zKLB2BVmqtpuZHW5mb8UMYWYys5OBTYCXY2bJ6SOSduz2zb5I3NCmAc8QGjHG9nu1ce/wrYBTYodwyevlIe7/Q9g25E1gSWCpuHGS9TfgBWAG4T6OixunEgsBmwPXF3hPr38f9/cS4fP2Au/dlxUIfy9FLV5WqAS9RbiHLwB/BxYjDPWfN2aoBvLPXjFPEsrPczFr2Xl0zFAV2Qq4Js+BkkYB3642TuleA7Y2s3tiB+nPzO6VtAFwLfAvkeMM5zjgj9280bdZe88VwFXAXWb230MdJGkewtyTXYEdgLnriVepjWMHcC4xEwh7Jt9tZncOdkA2dWQpYBvCCtEr1pYuDQ8DfwDuAB40s4cHO0jSYoT7OB7Yh7AdS+o+SbEKeq96mFAAvheYPMx386KEnpWPAhsAm9aSMA3PATcAdwOTCJ+35wc7MCvjrAmsTtj2aFtCA4hzA80ALgZuIZSdHxjqQEkLA3sAuxH+ptrgowWOPZTQOJuK6cB4M7s/dpDBmNmjkjYm7Kv+wdh5Olhb0qfM7MKibxxypfZscntKE/K78TjwQ+DXZvZG0TdnLWJ7EhZ8SL3HbFkze2KwH0haFXiw3jhdOcnMDo8dYjiS9ieNVYP3NLOL8h6cbY3xdIV5muBi4Fgzm1j0jdmXySHAZ0tPlZazgR9mc8oKk/QhQmHngFJT1WuameX+zpA0hVBh6gUzCXuSn5FnH91OJI0n/K3sUUawxDwFnANcbGZTRnKi7Nm+O7AfodLeS8aa2fS8B0s6APh5hXmaYCLwfTP7bTdvljQfsD/wLWD+MoPVbKaZDTuqNqsrPEVai1FuYmYThj8sLkkrA7cDC8bO0sGDZlb4+7tX56C/COxjZsuZ2RndVM4BzOwtMzvXzJYGjgReLTVlvTaIHcAlr81bMz5AaE3eo5vKOYCZTTCzvYFdSPtZ0a3rgdXM7IBuK+cAZjbRzL4AbAEM2guYgCUlLRs7RAMdD4wzs6NGWjkHMLObzWxPQo/w7SNOl4Yrgc3NbBkz+85IK+fw7rZHp5jZuoR7+V8jPadL0mPA9ma2TreVcwAze83MTgSWJXzmUzV31mA8nK+QVuX8oBQq5wDZyLumN8CuJmnDom/qxQr66cDyZlbqvnpmdgKwBvBomeetUZGhOs71kmuADc3sljJOZmZXAOsTCju94EXCaIytzewvZZ00W012XWByWees2WaxAzTIrcAqZvZ1M3up7JOb2RQz25jQkN5WE4B1zWxnM7u5qotk93I/YDng0qqu0yBtbngu4ltmtryZXV3WCc3sVTP7OmFP8FQbrTtWvCSNIWzlmIqbzOzM2CGKMLMbaP6o1P2LvqGXKuhvEXrNDzGz16u4gJlNIxS6Hqri/BVrw7xO58r2czPbrttRNkMxs0cJo1YaOb+rRE8DGxWZKlGEmT1LWEMjidb+AYrMkW5rJWE6cEi2r+6gaxCUKWtIP6fq69Tsn8B/EEb43FfXRc3scTP7OPAFwrQE104vAlua2XeruoCZ3UtYY6T0xrkaDDfl43PAfDXkKMNbhHVeUnQ4YZHZptorm+qQW69U0GcQHjCl9poPxsz+Rih4PVH1tUrmC1i5kWpbJeIEMzuwqpOb2YvAlqQ76mY4jxFGHnQ9nD2PbH7otkBtlZOSjC9wbBtXkr4TWNnMTq/5utvXfL0qPU8Yzv4dM/tnjABmdhawHvDXGNevQRs/e3k9C2xgZjdWfSEzm0QoO8+o+lolW2mYnx9US4pyfNvMnokdohtZOeCI2Dk6mBfYq8gbeqGC/jqwmZndUdcFs4L3fnVdryQflNTGbTBcfdpUkDnezCofDmtmLxN6oNrmOUKj6LN1XMzMZhJ6KlKyQrbyeC+6Adgqa9CujaTdgSXqvGaFbgfWNLPbYgfJpq6sC/wudpYKtK3hOa+nCJXz2qZiZX9HX63reiVZfqgfSOpbsyEFT5rZf8YOMRJmdgFhV5imKlQv7FRBb8ND6R1gt2z4TK2y+ZFJzePAt1JxDuB32by4WmTPigvqul5NtjSzJ+u8YNYDc2qd1yxBr6zM3t/lZrZV1qhSt09HuGYVLiUMaX8hdpA+ZjbDzD5B+1cw7wVvANvE6E3NRtREb3QqYClJQ21Z/flak4xM0pXzfr4XO0AHm2U7YuTSqYLeht6wI+sYmtPp+sDLEa9f1HKxAzgX2UPA3hGue0yEa1bl82UuBldQZfMkK7JazuPa0GAO8Cfg4zEunO3vvUuMa5fsAmAPM/tH7CCDyaYFpdZQ5ma1p5nFnLKQ0ghUY+jOrVS2VJ1OS9bmMLM/0ux1wLbNe2Cbe9BvMrOTYgbIFpa6OGaGgnweuut1nzGzN+u+qJlNBX5V93UrcKWZ/SLWxc3sf4ATY12/C3kr6G1oML/FzHaIWLHcDSi0SE8D3Qh8NtZ887zM7EvAhbFzuK6cbGZXxQyQLaJayq4pNXlf2VnSPqSzONzvYpR7KvTr2AE6KKWCnrLXgc/EDpFJaehq7qEXzrXQf9S5CvIgUqpYDuXw2AGAk2MHKGDVnMel3mD+LLB75AypD2//K2HK3juxg+S0L3B37BCukKnAUbFDZFIvO0cZKdSlrve0b6jzYwfoYOu8B7a1gv6Nuhef6eAmwkqrKVgkdgCXtJQrEVPN7DsxA2SNA1NjZhihn1S9YnseZvYE6Wy71is96LtmCyJGIWl+YMdY1y/BTGCXqraIrYKZ/Z0wauHF2FlGKPXPXhH/amZvxQ6RuYCwhWAKZik7SzJgm0hZivpf4LrYIcqULWxY+badXVooWzxwWG2cg36/mZ0WO0QfMxNwSewcOS0cO4BLWqrPDIAvxg6QSWlKzEA/jB2gn8tiB8hpsZx7o6bc+PU9M7sncoatIl9/pA6JPCe4K1lHSeojF1L+7BVxoZndHjtEHzN7Bbg5do6cBpad1wfGxAjShTvN7H9jh6jA9bEDdJCr8aaNPehN3KKh9lXku+Q96K4XTTCza2KHyKRSIBnoZw0atQRp3cfFYweo0BM0Y+G+TWMHGIGLzezc2CG6ZWbXAafEzuGGVdvOJQWkWnZOqUHwztgBKtKYxqZBbJHnoLYtEvdY9mXQNJNjB8jJK+huJFJt8GtCBaJPKkOzB2ra1koTgbdjh8gpTwU91dEpBzakd2Z87ABdmgl8OXaIEhwDvBI7hBvSpWb2VOwQg0i17JxSBT2VRpCimvx75dpedai9+yDNAsHxsQMMYVLsADn5InFuJFJZvKi/SWZ2dewQfczsdUlTgeVjZyngwciL672Pmb0t6V5gg9hZclgsxzEpNpjfaGbXxg6Rba+2VuwcXTquYSNTupI9144Bfho7ixtUUxfWTKXsPHCIe0oV9CUlbR47RAU61W9jW0rSGDOb0emgTr9AagWCmUAjh4GZ2VuSHmXovRIbQ9KcDenxcK4OTSwwTiatCnpTV0ydTBoV9Lb2oDdhRX8Ivecpju6ZTjt2duhzJvAt8jVIufo8amY3xQ4xhAcJDf9N//y+u52apNRG6/w4doAetRLQsWOj6X/0RZxvZjNjh+igicOHBjN37ADO1WQGcF7sEIOIvhJ6QVfGDjCEVO7jojmOSa3B/AYzuz92iMwmsQN06fSGl2kKMbN/kOZc9BQbx4o4PXaAoWSdRc/FzpFD/3JzruHLruetMtwBbVrF/ZzYAYbxWuwAOaWy8qRzI/WHhhaAn44doIBpZjYxdoghpHIfF4gdoAKN2UkFWCN2gC79JHaACpwRO4B7n1/GDjCMFNYu6F9uHrbi5Ryw8nAHtGWRuNeAO2KHGIZX0J1rlqZuxZXSnNNbYgfoIJX7OE+OY1JqMH/OzJq0teiKsQN04TozmxY7RNnM7EXg97FzFJRSWbioO7L/T5oshbKzV9BdUSOqoKfkymy/8SZ7NXaAnLyC7rqVWkHmqtgBhpDKswKa3TCayn2cN8cxKX22fh07wACNX/tlEL+JHaBCF8YO4N7V1O/A/pJ4jkvqG+a+UtQgLhXLDHdAW1ZxvyF2gBySeMjgc9Bd91J6ZtxsZtNjhxhCCkP6+twTO0AHqdzHPBX0lPwqdoA+kpYFZo+dowu/ix2gQn+IHcC9K4Wy88uxA+Q0RtI/yVHxcg5YcLgD2jLEvcn73fV5M3aAnOaKHcAlK6UROdfHDtBBKo15AH+NHaCDVO5jmyrozzVocThIc3j7fWaWyt9uYdnWQnfGzuGANMrOqewqNCewWuwQLhnzD3dASgXqobwNTIkdwjmX1D7ot8UO0MFLsQPk9LKZNTarmaUwdxHyzUFPxXWxAwyQ4vD2Jj+byjIhdgDHQ2aWSsdRKpaOHcAlY9jtJtuwivtDZvZ27BA5pHI/U8npmielBr8m9xyk8DwDeDJ2gBzeih0ghzlyHJPKc7lpiwbm2cKuaW6PHaAG3oMe3+TYAXJK5dn3Dj5F1BUgaVSnn7dhiHsq+4uncj9TyemaJ5Uv0icb3ruayn1MYX/aVO7lcFJ5Lk+KHWCAjgWghnoodoAaNHlqTK/wsnO5ZiPN542Lp+Mw95R6vIaSQi+Oc645nogdoCWavj2Pq98DsQMMkOKaKn+JHaAGD8cO4LzsXAGvoLsiuq6gp9Lz8HzsADmlcj9TyemaJ5WW7qb3HKRyH1PImULGPFJ4Lr9sZm/EDjFAahX0F8zs77FDVC37HVMYgdNmXnYu1zuEheKcy2tspx+2YYh7Kh/eVO5nKjmd61aTh7enpNM2na5cKTyXm1ixTK1H6x+xA9TohdgBepyXncs1GzA6dgiXlI7rDbVhiHsqDxnn2i6VVdyb/oWfyn1MoYKewr1sy3fYP2MHGERqPei9xKfIxNWW506TpNYg6OKa0emHbRjinkIBLHWp/C2kkrPpFcQ+Re9nKr/X7LEDDCOV+5hCzrZkTOHZ1sTv4hQakfprQ6dJXq/EDtDjUnimQFo58+zI4VyfjlPC2jDEPZWcKUvlHqeSM5VhUEXvZyr3v+mF4FTuYwo5U8iYRwq/RxM/VzNjByhoTOwANfItqeJK4ZkCaeX0feVdEV33oDvnqtFLhTDnnIulaYvWDWee2AFqNG/sAM6VLLXnjYtreqcfegXd5ZHSEKMUpFIIS+V+OufcYJIrMEvquLJvi3TcYsi5BHXsEXWun7fNrOO0MK+guzxSGmKUgqVjB8gplfvpnHODSa6CDiwbO0BNVogdwLmSpfi8cXEM25iT2gIqzrXBMrEDOOdcD0ixwLwCcH/sEFWStBS+wr5rn9SeN9OAs2KH6FEdh7eDV9BdPqkMdU4lZyo9JKncT+ecG8zrsQN0YaXYAWqwSuwABfj3oMsrtSHuL5rZd2KHcIPzIe4uj1SGOjc+p6TRpNOD3vj76ZxzHTwTO0AXNosdoAbjYwcowL8HXV6pNQimsqNQT/IKussjlRbkFHJ+JHaAAlK4n845N5RHYwfowiaS2l422yJ2AOcq8FTsAAUtEDuAG1rbvwRcOd6OHSCnFFoD148dwDnneoGZPR47QxfmATaKHaIqkhYANo6dowBvqHa5mNkLpNWLvkg2qtM10P8BAAD//+3debAldXmH8edNXFgNUmoAl8BIjApjJICChpgIUohFClFRg4FguUSjuBJiRcQQMEFRNKIIMVUuUUtwXwJRYRRFHSWsMy7sII7igA7iOIjCmz/6jDXILGfpc97uc59P1ak7U3O6+3tvzT3d72+1QNcw7qgOMKQ+bE+zf3WAETi0T1LfXVEdYAzPqQ4wRc+oDjAi74MaRd8+b/6kOoDWzwJdw/hVdYAhbV0dYGMy8z70a+6dJPVdH4e5PyMz57UwfG51AGmK+vZ5s1t1AK2fBbqG0ZcCfavqAJtwIHCf6hAjcGifpL7r2wMzwHbAQdUh2paZi3D+ueZb33rQ96gOoPWzQNcmRcSt1RmG1OkedOCw6gCStMBcUh1gTEdVB5iCV+CQcc23vhXoj68OoPWzQNc82bk6wIZk5tbAM6tzSNICc351gDHtm5m7VIdoS2beD3h+dY4xOJJMo/hedYAR7Z6Z21eH0D1ZoGtYt1UHGMI2mfmw6hAb8ILqAGOwp0NSr0XE1cAt1TnGdFJ1gBa9ge5PQ1sf74MaxcXAL6pDjOjg6gC6Jwt0Devm6gBDWlwd4Hdl5r2A11bnkKQF6tzqAGN6Wmb2fmHRzNyZ+RyyL91NRNwFnFedY0TPqw4wqcz8SPbD0FMgLNA1rJXVAYbUuQIdOBLYoTrEGBzaJ2kefKU6wAROqw7QgtOAe1eHGJP3QY2qbw2CT8jMR1eHmNAfVgcY0jnDvtECXcPqSw/6E6oDrCubuecnVOeQpAXsa9UBJvCozOztPSQzjwT2q84xAYe4a1R960EHeGl1gAntXh1gSF8Y9o0W6BpWXwr0gzLz/tUh1nEi8KDqEJK0UEXEZcAN1Tkm8M+Z2ZcH0N/KzB2At1fnmJA96BpJRCwDbqrOMaLnZ+YDq0OMIzP3AO5XnWNIS4Z9owW6htWXAh06Mp8mM/8ceHl1DkkSZ1YHmND/ZOZDq0MMKzO3oXkY7cuD84bYg65x9G2Y++bAq6tDjOmQ6gBDWhIRq4d9swW6hnVddYARHFEdIDM3Az5UnWNCPphImhcfqQ4woQcBX8nMzo/IysytgC8Bj6jO0gJ70DWOs6oDjOEVfWoEXEf5M/+Qzh7lzRboGtaV1QFGsHtm7luc4QNAV7d8k6QFJSIuAq6qzjGhnYBvZGZnF0TKZr/z8+jPnNBNsaFaI4uITwGrqnOMaHPgPdUhRpGZz6U/izCPNIrLAl3D6lOBDnBGZt634sKZ+XrgWRXXliRt0IerA7RgEXBBZj6kOsjvysxtgS8DexZHaZM96BpXH6fVHJiZh1WHGEZmbg6cVJ1jSOdHxPWjHGCBrmFdB9xZHWIEi4BjZn3RzHwhcPysrytJ2qS+D3Nf6+HAtzJz1+oga2XmjsBSYLfiKFJX/Hd1gDGdNvh97rpTgL4MyX//qAdYoGsoEXEn/etFf91gobaZyMzXAmcwP0Pi7DmQNDci4ns0PbzzYHuanvQnVQcZrKK8FNi5OovUFRHxVfq5e8TWwJmDtZQ6KTOfA7y4OseQbgc+NupBFugaxXeqA4xoM+ALmTn1PVgz8wPAW6Z9HUnSRN5ZHaBF9wOWZOYZFduLZuYWmXkK8E3mdzvReWlwV42+9qLvCXw0MztXJ2bmXozRI13o0xHx81EP6twPXp22tDrAGDYHvpiZU9mGITP3yszvAH87jfNLktoTEZ8ARpoL2HEBvBD4fma+ZjAvc+oy82XAFcArgd+fxTWLOJJMk3h3dYAJ/DVwenWIdWXmLsDngftUZxnBGeMcZIGuUfSxQF/r45l5dltz9jLzAZl5OvAN4FFtnFOSNBOnVgeYggcCJwM/zMx/Gqym3qrM3Cozj87Mm2hGIjy47Wt0kD3oGltE/BB4R3WOCbwgMz85q4a/jRkU50uAbauzjODiiDhvnAMt0DWKC6sDTOgA4PLMfH9mHpSZW496gsEDyquAa4EXtZ5QkjRt7wXWVIeYkvsD/wasyMxTMvPRk54wMx+ZmScDK4A3M7/D2dfHHnRN6k0085D76mDg/MxcVBUgM59H00n4wKoMYxp7lfl7tZlC8y0iVmfmJcBjq7NM6PDBi8y8kKZF7hqaYY8/Bm6MiJVr35yZewJ7A/sDT5t5WklSayJiVWa+F3h5dZYp2pJm+PkrM/NHwDk0jezLgcsi4mfrO2gwl30xsAvNXuYHsDB6yjfEHnRNJCJ+kpn/AfxjdZYJ7AEsy8yjI+Jds7poZi4G3go8ZVbXbNG1EfHRcQ+2QNeovk7/C/R17TF43U2mjeaSNMeOA46gWWht3m0PHDl4Ad7jpBl7C/BSYKvqIBPYHDh10Jt9eERMbWenzNySZuTBy+jvaO+3TXJwX79p1flcdQDNjD0HkubSoAf5xOockuZfRNxMMz1kHuwFXJGZ7x70cLcmMx+RmcfTLEB5FP2tU28C/muSE/T1G1edc5nfuXuSpIXjFOZrRXdJHRUR/wp8vzpHi14CXJaZ387MN2bm3uOcJDP3yMxjM/Nimp/PscAObQYtcHRETFQrOcRdI4mIOzLzs8Ch1VkkSRpXRPw6M48GzqzOImlBOAL4ZnWIlq2dKnpcZt4OXEqz1sVVwMrB63aaonu7wdftgYcCj6sIPGVfj4gPTnoSC3SN41NYoEuSei4izsrMC4AnVmdRJzlZX62JiKWZ+Q7gFdVZpmQz4PGD10KUNCMLJuYQd43DeegLgw8mkhaCI+j3NkiaHtdiUdteB1xXHUJTcXpEXNbGiSzQNbKIuA14f3UOTZ0PJpLmXkRcDRxdnUPS/BvMTX5BdQ617kaaxpdWWKBrXBOtTihJUldExKnAl6tzSJp/EXEucEJ1DrXmN8AhEbGqrRNaoGssEfFV4NrqHJIkteRvgJ9Xh5C0ILwB+FJ1CLXi2Ij4dpsntEDXJN5THUCSpDZExI9oaYEfzQ3XYtFUREQCzwRuqM6iiSwBTmr7pBbomsTpwK3VISRJakNEfBh4S3UOdYZrsWhqIuJW4KDqHBrb9cCzBo0trbJA19gGHywnV+eQJKlFx+BuJZJmYLDq92HVOTSynwEHRMQt0zi5BbomdQowlf+ckiTN2qA35FDgwuoskubfYOTO83BKRV/cQVOcf29aF7BA10QiYjVwYnUOSZLaMtgK6am4X7GkGYiIDwHPplkRXN2VNMPavzXNi1iga2IRcQpwRXUOtc6WXEkLVkTcDOwP3FSdRWW8D2pmIuIs4OnVObRRL4uIz0z7IhboastzqwP0yI3VASRJmxYRVwL7AD+uziJp/kXE54D9gDXVWXQ3dwKHRsS7Z3ExC3S1IiIuAo6rztED5wBvrA4hSRqORXrrLgA+Vh1C6qqIOBfYGzt0uuJ2mjnnZ83qghboatMJwP9Vh+iwi2mGLvVlyJzby0gSEBFXYZHehluAZ+B9UNqoiLgUeCzwzeosC9xPgX0i4kuzvKgFuloTEXcBBwM3V2fpoKuBp0TE7dVBJEmjW6dIv6E6S0+tAZ4WEc7pl4Yw2MJrH2Amw6p1D5cDu0XEzHf0sEBXqyLiRuDA6hwdcxXw5HX2SuxLz0FfckrSTAyK9D8FzqvO0jNrtyVaWh1E6pOI+E1E/ANwOM3vkWbjw8CeEVHSIGuBrtZFxLeBZ1bn6IjLgSf+zi94X4bM9SWnJM1MRKwCngK8uTpLT6wBDoyI86uDSH0VER+kGfJ+SXWWOXcH8JKIOCwiflUVwgJdUxERHwdeXZ2j2NdpivOfVAcZkz3okrQeEXFXRBxDM5/a1ZY3bDXNCLJzq4NIfRcR342I3YDX0CxcpnZdAewdEe+pDmKBrqkZ7I9+ZHWOImcD+0XEbev5NwtfSZoDEfEJ4HHARdVZOugq4PERsb5FrvpyH+xLTi0gEfE24JHAkuosc+JOmhFRiwe7UpWzQNdURcT7aIYC/rI4yqzcARxNsxDOhnpV+jJ0vC85JalMRCyLiN2B11Vn6ZAzgT+LiOXVQaR5FBHXR8STaTrCVlTn6bGraUa7HhMRnZnjb4GuqRtsTbAP87+f49regpMjYh5a3efhe5CkmYiIfwcezsLeFmkFzWJwz97ACLK1+tIA3JecWqAi4n0R8WDg5bgN5ChuA14P7NrFxSst0DUTgyEji4FPVmeZktNohsYMs3iHha8kzaGIuCYi9gZeBKyszjNjJwJ/HBH/O8R7+3If7EtOLXARcSqwI836T31d+2gW1tAMZ39YRJzY1e2PLdA1MxGxKiIOAV7M/Cyq80WawvylI/yS96VFvi85JalTIuI/gT+ieVi+ZRNv77vPAQ+PiNdHxEKZziZ1TkT8arD+007Aa4FriyN1zWnAjoPh7Kuqw2yMBbpmLiLOoNlH9oPVWSawHNg/IvaPiGUjHtuXFvm+5JSkzomINYOH5R2BY4FOPxCO6E6afYJ3iYiDIuKaEY/vSwNwX3JKvxURv4yIt9JMudmPZk2IzsyvnrFlwFHANoPOtF6MLrBAV4mIuDIiDqf58Di9Os8IlgHPiYhdI+KL1WEkSd0WEb+IiBNoerWOod+9WquBtwOLBvsEf2fM8/SlAbgvOaV7iIiMiHMj4tnAQ2gWMf5+caxZWA18gGbLtMUR8c6IuLU61Cgs0FVqMF/v74EHA8cBNxRH2pDPAPsOftE/Wh1GktQvg2leb46IRcC+9GsU2deAlwA7RMSrIqKr92pJ6xERKweLGD8SeCzNAmlLgbtqk7XmNuBDwMHAthFxxAa2eOyFe23k364H/mVWQSZwfnWAIV1AP36eJS37EbECOB44PjP/Cvg74FBgs4o8A9cBnwXeFRFttjheQj/+L4zaM7KKfnxfF1YH2JiIWJ2Zffg5jttzNksnAPeuDrEJNw3xnncBD5p2kAn1qneiWkScB5yXmUcBRwCHAH9Rm+puVtLssbwE+HxE/KDl858FfLflc7ZuEyvRr89F9OM+2IfPb4BPA23/35uGn1cHGEVEXApcCpyYmQ8Ang48Fdgf2LIy24iW09SBX4iIT1WHaZNza9RZmXlf4AnAk4C/BPYC7jvFS64BvgycA5wdEVdO8VqSJP1WZv4BcABND9ABwDYzvPw1NMXlV4Al7l8uLUyZuSuwtpd98eC1U2moxiqaDpalwFeBb0RErxpGRmGBrl7JzH2APYBdaRaa24XxetlXAJfR9GYvB5YNuUWaJElTl5l7A48BHg08avB6yASnvJGmZ/wHND2oa1/Lu7rVkKR6mbkFzTP3zsAimvWjdhr8fbsWL/ULmi3iVgBXA5fT9PQvj4gftXidzrNAV+9l5mNoPiC2oBmas+U6f/41zQPJCuCnwE8i4vqiqJIkjS0zt6Ip2Leluc9tAWy+zp9/j+YB96bB15U0971Rh2pL0iZl5mY0Bft2NJ9Fm23glTSLt/1y8HU1zbzxW2k+o1zXYh0W6JIkSZIkdYAFuiRJkiRJHWCBLkmSJElSB1igS5IkSZLUARbokiRJkiR1gAW6JEmSJEkdYIEuSZIkSVIHWKBLkiRJktQBFuiSJEmSJHWABbokSZIkSR1ggS5JkiRJUgdYoEuSJEmS1AEW6JIkSZIkdYAFuiRJkiRJHWCBLkmSJElSB1igS5IkSZLUARbokiRJkiR1gAW6JEmSJEkdYIEuSZIkSVIHWKBLkiRJktQB/w9LUsbEFo/kAgAAAABJRU5ErkJggg=='; // your base64 string
 
    // doc.addImage(imageData, format, x, y, width, height)
    doc.addImage(logoBase64, 'PNG', 20, 20, 60, 15);
 
    
    // Tagline
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("www.humrahe.com", 20, 42);
    
    // Reset colors
    doc.setTextColor(26, 27, 46);
    
    // Title with decorative elements
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("Certificate of Association", pageWidth / 2, 75, { align: "center" });
    
    // Decorative line under title
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.line(60, 80, pageWidth - 60, 80);
    
    // Reference number and date
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    const today = new Date();
    const token = localStorage.getItem("humrahe_token");
    const raheID = token ? JSON.parse(atob(token.split('.')[1])).sub : 0;
 
    const refNo = `HML-${String(raheID).padStart(8, '0')}`;
    // Result: "HML-00000123"
 
    
    doc.text(refNo, 20, 95);
    doc.text(`Date: ${format(today, "dd MMMM yyyy")}`, pageWidth - 20, 95, { align: "right" });
    
    // Main content
    doc.setTextColor(26, 27, 46);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    
    const joiningDate = profile.created_at ? formatJoiningDate(profile.created_at) : "N/A";
    const cities = form.cities.filter(c => c.cityName).map(c => c.cityName).join(', ') || "various cities";
    
    const bodyText = `To whom it may concern,
 
This letter serves to confirm that ${form.full_name} is officially registered as a Local Discovery Guide (Rahe) with Humrahe Limited.
 
${form.full_name} has been associated with Humrahe Limited since ${joiningDate} and operates on a freelance basis, providing local discovery services through our platform.
 
As a Rahe, they offer their experience, recommendations, and knowledge to travelers and visitors, delivering authentic local experiences in their city.
 
This certificate validates their status as an active member of the Humrahe Limited Rahe community.`;
    
    const splitText = doc.splitTextToSize(bodyText, pageWidth - 40);
    doc.text(splitText, 20, 115);
    
    // Calculate Y position after text
    const textHeight = splitText.length * 6;
    const yPos = 115 + textHeight + 15;
    
    // Signature section with box
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.roundedRect(20, yPos, 80, 35, 3, 3);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Authorized Signature", 25, yPos + 8);
    
    doc.setFontSize(11);
    doc.setTextColor(26, 27, 46);
    doc.setFont("helvetica", "bold");
    doc.text("Humrahe Limited", 25, yPos + 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("United Kingdom", 25, yPos + 27);
    
    // Footer
    doc.setFillColor(245, 245, 245);
    doc.rect(0, pageHeight - 25, pageWidth, 25, 'F');
    
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("This is an electronically generated certificate and is valid without a physical signature.", pageWidth / 2, pageHeight - 15, { align: "center" });
    doc.text("www.humrahe.com | hello@humrahe.com", pageWidth / 2, pageHeight - 8, { align: "center" });
    
    // Save the PDF
    doc.save(`RahePortal_Association_Letter_${form.full_name.replace(/\s+/g, '_')}.pdf`);
    toast.success("Association letter downloaded!");
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1A1B2E] border-t-transparent" />
      </div>
    );
  }

  const age = form.dob ? (() => {
    const birth = new Date(form.dob);
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) years--;
    return years > 0 ? years : null;
  })() : null;

  return (
    <div className="animate-slideUp space-y-6 max-w-4xl" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{t("profile.description")}</p>
        <Button
          variant="outline"
          onClick={generateAssociationLetter}
          disabled={!profile?.profile_complete}
          className={`gap-2 ${!profile?.profile_complete ? "opacity-50 cursor-not-allowed" : ""}`}
          data-testid="download-letter-btn"
        >
          <FileText className="w-4 h-4" />
          Download Association Letter
        </Button>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 space-y-6">
        {/* Profile Picture & Joining Date */}
        <section className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <div 
                className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-slate-200"
                data-testid="profile-picture"
              >
                {pictureObjectUrl ? (
                  <img src={pictureObjectUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-slate-400" />
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#1A1B2E] text-white flex items-center justify-center hover:bg-[#2D2E45] transition-colors"
                data-testid="upload-picture-btn"
              >
                <Camera className="w-4 h-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">{t("profile.profilePicture")}</h3>
              <p className="text-sm text-slate-500 mt-1">JPG, PNG (max 5MB)</p>
              {form.has_picture && (
                <button
                  onClick={() => { setForm(f => ({ ...f, has_picture: false })); setPictureObjectUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); }}
                  className="text-sm text-red-500 hover:text-red-600 mt-2"
                >
                  {t("profile.removePhoto")}
                </button>
              )}
            </div>
          </div>
          
          {/* Joining Date */}
          {profile.created_at && (
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg border border-slate-100">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Member Since</p>
                <p className="text-sm font-semibold text-slate-900" data-testid="joining-date">
                  {formatJoiningDate(profile.created_at)}
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Personal Information */}
        <section className="space-y-4">
          <h3 className="text-base font-semibold text-slate-900 border-b border-slate-100 pb-2">
            {t("profile.personalInfo")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={errors.full_name ? "text-red-500" : ""}>{t("profile.fullName")} *</Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                className={`mt-1.5 ${errors.full_name ? "border-red-500" : ""}`}
                data-testid="profile-fullname"
              />
            </div>
            <div>
              <Label className={errors.email ? "text-red-500" : ""}>{t("profile.email")}</Label>
              <Input
                type="email"
                value={form.email}
                disabled
                className="mt-1.5 bg-slate-50 text-slate-500 cursor-not-allowed"
                data-testid="profile-email"
              />
              <p className="text-xs text-slate-400 mt-1">Email address cannot be changed</p>
            </div>
            <div>
              <Label className={errors.phone ? "text-red-500" : ""}>{t("profile.phone")} *</Label>
              <div className="flex gap-2 mt-1.5">
                <DialCodePicker
                  value={form.phone.code}
                  onChange={(v) => setForm((f) => ({ ...f, phone: { ...f.phone, code: v } }))}
                  testId="profile-phone-code"
                />
                <Input
                  value={form.phone.number}
                  onChange={(e) => setForm((f) => ({ ...f, phone: { ...f.phone, number: e.target.value } }))}
                  className={`flex-1 ${errors.phone ? "border-red-500" : ""}`}
                  data-testid="profile-phone"
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div>
              <Label className={errors.whatsapp ? "text-red-500" : ""}>{t("profile.whatsapp")} *</Label>
              <div className="flex gap-2 mt-1.5">
                <DialCodePicker
                  value={form.whatsapp.code}
                  onChange={(v) => setForm((f) => ({ ...f, whatsapp: { ...f.whatsapp, code: v } }))}
                  testId="profile-whatsapp-code"
                />
                <Input
                  value={form.whatsapp.number}
                  onChange={(e) => setForm((f) => ({ ...f, whatsapp: { ...f.whatsapp, number: e.target.value } }))}
                  className={`flex-1 ${errors.whatsapp ? "border-red-500" : ""}`}
                  data-testid="profile-whatsapp"
                  placeholder="WhatsApp number"
                />
              </div>
            </div>
            <div>
              <Label className={errors.dob ? "text-red-500" : ""}>{t("profile.dob")} *</Label>
              <Input
                type="date"
                value={form.dob}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm((f) => ({ ...f, dob: val }));
                }}
                max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split("T")[0]; })()}
                min={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 100); return d.toISOString().split("T")[0]; })()}
                className={`mt-1.5 ${errors.dob ? "border-red-500" : ""}`}
                data-testid="profile-dob"
              />
              {age !== null && age < 18 && (
                <p className="text-xs text-red-500 mt-1">You must be at least 18 years old</p>
              )}
              {age !== null && age >= 18 && (
                <p className="text-sm text-slate-500 mt-1.5 font-medium" data-testid="calculated-age">
                  {t("profile.age")}: <span className="text-slate-900">{age} {t("profile.years")}</span>
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Languages */}
        <section className="space-y-4">
          <h3 className={`text-base font-semibold border-b border-slate-100 pb-2 ${errors.languages ? "text-red-500" : "text-slate-900"}`}>
            {t("profile.languages")} *
          </h3>
          <p className="text-xs text-slate-500">At least one language with proficiency is required</p>
          {form.languages.map((lang, i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">{t("profile.language")} *</Label>
                <SearchableSelect
                  options={languageOptions.map((l) => ({ value: l.name, label: l.name }))}
                  value={lang.language}
                  onChange={(v) => updateLanguage(i, "language", v)}
                  placeholder="Select language"
                  searchPlaceholder="Search languages…"
                  className="mt-1"
                  testId={`lang-select-${i}`}
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs">{t("profile.proficiency")} *</Label>
                <Select value={lang.proficiency} onValueChange={(v) => updateLanguage(i, "proficiency", v)}>
                  <SelectTrigger className="mt-1" data-testid={`prof-select-${i}`}>
                    <SelectValue placeholder="Select proficiency" />
                  </SelectTrigger>
                  <SelectContent>
                    {proficiencyOptions.map((p) => (
                      <SelectItem key={p.id} value={p.level}>{p.level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.languages.length > 1 && (
                <button
                  onClick={() => setForm((f) => ({ ...f, languages: f.languages.filter((_, j) => j !== i) }))}
                  className="text-red-400 hover:text-red-600 p-2"
                  data-testid={`remove-lang-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() => canAddLanguage() && setForm((f) => ({ ...f, languages: [...f.languages, { language: "", proficiency: "" }] }))}
            className={`text-sm font-medium flex items-center gap-1 ${canAddLanguage() ? "text-blue-600 hover:underline" : "text-slate-300 cursor-not-allowed"}`}
            disabled={!canAddLanguage()}
            data-testid="add-language-btn"
          >
            <Plus className="w-4 h-4" /> {t("profile.addLanguage")}
          </button>
        </section>

        {/* Cities */}
        <section className="space-y-4">
          <h3 className={`text-base font-semibold border-b border-slate-100 pb-2 ${errors.cities ? "text-red-500" : "text-slate-900"}`}>
            {t("profile.primaryCity")} *
          </h3>
          <p className="text-xs text-slate-500">Primary city and years in city are required</p>
          {form.cities.map((city, i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">{i === 0 ? t("profile.primaryCity") : t("profile.secondaryCity")} *</Label>
                <SearchableSelect
                  options={cityOptions.map((c) => ({ value: c.id, label: c.name }))}
                  value={city.cityId}
                  onChange={(v) => {
                    const selected = cityOptions.find((c) => c.id === v);
                    const cities = [...form.cities];
                    cities[i] = { ...cities[i], cityId: selected?.id ?? null, cityName: selected?.name ?? "" };
                    setForm((f) => ({ ...f, cities }));
                  }}
                  placeholder="Select your city"
                  searchPlaceholder="Search cities…"
                  className="mt-1"
                  testId={`city-select-${i}`}
                  virtualized
                />
              </div>
              <div className="w-32">
                <Label className="text-xs">{t("profile.yearsInCity")} *</Label>
                <Input
                  type="number"
                  value={city.years}
                  min="0"
                  step="1"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || (/^\d+$/.test(value) && Number(value) >= 0)) {
                      updateCity(i, "years", value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (["-", "+", "e", "E", "."].includes(e.key)) e.preventDefault();
                  }}
                  className="mt-1"
                  placeholder="Years"
                  data-testid={`city-years-${i}`}
                />
              </div>
              {form.cities.length > 1 && (
                <button
                  onClick={() => setForm((f) => ({ ...f, cities: f.cities.filter((_, j) => j !== i) }))}
                  className="text-red-400 hover:text-red-600 p-2"
                  data-testid={`remove-city-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() => canAddCity() && setForm((f) => ({ ...f, cities: [...f.cities, { cityId: null, cityName: "", years: "" }] }))}
            className={`text-sm font-medium flex items-center gap-1 ${canAddCity() ? "text-blue-600 hover:underline" : "text-slate-300 cursor-not-allowed"}`}
            disabled={!canAddCity()}
            data-testid="add-city-btn"
          >
            <Plus className="w-4 h-4" /> {t("profile.addCity")}
          </button>
        </section>

        {/* Tour Types */}
        <section className="space-y-4">
          <h3 className={`text-base font-semibold border-b border-slate-100 pb-2 ${errors.tour_types ? "text-red-500" : "text-slate-900"}`}>
            {t("profile.tourTypes")} *
          </h3>
          <div className="flex flex-wrap gap-2">
            {tourTypeOptions.map((type) => (
              <button
                key={type.code}
                onClick={() => toggleTourType(type.code)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  form.tour_types.includes(type.code)
                    ? "bg-[#1A1B2E] text-white border-[#1A1B2E]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`tour-type-${type.code}`}
              >
                {type.name}
              </button>
            ))}
          </div>
          {form.tour_types.includes("OTHER") && (
            <div className="mt-3">
              <input
                type="text"
                placeholder="Please specify your tour type"
                value={form.other_tour_type || ""}
                onChange={(e) => setForm((f) => ({ ...f, other_tour_type: e.target.value }))}
                className={`w-full max-w-sm px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-[#1A1B2E] ${errors.other_tour_type ? "border-red-400" : "border-slate-200"}`}
                data-testid="other-tour-type-input"
              />
              {errors.other_tour_type && (
                <p className="text-xs text-red-500 mt-1">Please specify your tour type</p>
              )}
            </div>
          )}
        </section>

        {/* Certification */}
        <section className="space-y-4">
          <h3 className={`text-base font-semibold border-b border-slate-100 pb-2 ${errors.is_certified ? "text-red-500" : "text-slate-900"}`}>
            {t("profile.certification")}
          </h3>
          <p className="text-sm text-slate-600">
            {t("profile.certificationQuestion")} *
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setForm((f) => ({ ...f, is_certified: true }))}
              className={`px-6 py-2 rounded-lg text-sm font-medium border transition-all ${
                form.is_certified === true
                  ? "bg-[#1A1B2E] text-white border-[#1A1B2E]"
                  : "bg-white text-slate-700 border-slate-200"
              }`}
              data-testid="cert-yes"
            >
              {t("profile.yes")}
            </button>
            <button
              onClick={() => setForm((f) => ({ ...f, is_certified: false }))}
              className={`px-6 py-2 rounded-lg text-sm font-medium border transition-all ${
                form.is_certified === false
                  ? "bg-[#1A1B2E] text-white border-[#1A1B2E]"
                  : "bg-white text-slate-700 border-slate-200"
              }`}
              data-testid="cert-no"
            >
              {t("profile.no")}
            </button>
          </div>

          {form.is_certified && (
            <div className="animate-fadeIn">
              <Label>{t("profile.uploadProof")} *</Label>
              {form.certification_file ? (
                <div className="mt-2 border-2 border-emerald-200 bg-emerald-50 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-700">{form.certification_filename || "Certification uploaded"}</p>
                      <p className="text-xs text-emerald-500">File uploaded successfully</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setForm(f => ({ ...f, certification_file: null, certification_filename: null }))}
                    className="text-red-400 hover:text-red-600 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
                  data-testid="upload-cert"
                  onClick={() => certFileRef.current?.click()}
                >
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">{t("profile.uploadHint")}</p>
                  <p className="text-xs text-slate-400 mt-1">{t("profile.uploadFormat")}</p>
                </div>
              )}
              <input
                ref={certFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) {
                    toast.error("File size must be less than 5MB");
                    return;
                  }
                  const formData = new FormData();
                  formData.append("file", file);
                  setCertUploading(true);
                  try {
                    const { data } = await nestApi.post("/rahe-portal/profile/certification", formData, {
                      headers: { "Content-Type": "multipart/form-data" }
                    });
                    setForm(f => ({
                      ...f,
                      certification_file: data.url,
                      certification_filename: data.filename,
                      is_certified: true,
                    }));
                    toast.success("Certification uploaded successfully!");
                  } catch {
                    toast.error("Failed to upload certification");
                  } finally {
                    setCertUploading(false);
                  }
                }}
                className="hidden"
              />
            </div>
          )}
        </section>

        {/* Agreement */}
        <div className={`flex items-start gap-3 pt-4 border-t border-slate-100 ${errors.certify_info ? "text-red-500" : ""}`}>
          <Checkbox
            checked={form.certify_info}
            onCheckedChange={(v) => setForm((f) => ({ ...f, certify_info: v }))}
            data-testid="certify-checkbox"
          />
          <Label className={`text-sm leading-relaxed cursor-pointer ${errors.certify_info ? "text-red-500" : "text-slate-600"}`}>
            {t("profile.certifyInfo")} *
          </Label>
        </div>

        {/* Save */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#1A1B2E] hover:bg-[#2D2E45] text-white px-8"
          data-testid="save-profile-btn"
        >
          {saving ? t("profile.saving") : t("profile.save")}
        </Button>
      </div>
    </div>
  );
}
